// src/types/ClientData.ts
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth — from FFM Project State v10.
// Every field that will ever be needed is defined here.
// No new fields should be added to components — they go here first.
// ─────────────────────────────────────────────────────────────────────────────

import { TaxYear } from '../taxConfig';

// ── Sub-types ────────────────────────────────────────────────────────────────

export interface VoyantCapture {
  retirementAge: number | null;
  potAtRetirement: number | null;
  annualIncomeAchievable: number | null;
  voyantLumpSumNeeded: number | null;
  voyantAnnualSavingsNeeded: number | null;
  capturedDate: string;
  // Balance sheet at target age (from Voyant)
  voyantAssetsAtTarget: number | null;
  voyantLiabilitiesAtTarget: number | null;
  voyantNetWorthAtTarget: number | null;
}

export interface PensionStep {
  fromYear: number;           // calendar year steps take effect
  monthlyAmount: number;
}

export interface DLAPayment {
  year: number;
  month: string;              // 'Dec' etc.
  amount: number;
}

export interface LeverDecision {
  leverKey: LeverKey;
  applied: boolean;
  monthlyAmount: number;
  startDate: string;          // 'Apr 2026'
  completionDate?: string;
  gapClosedAmount: number;    // £ per year
  gapClosedPct: number;       // running % of total gap
  taxSavedThisYear: number;
  taxSavedToRetirement: number;
}

export type LeverKey =
  | 'lever0_extraction'
  | 'lever1_debt'
  | 'lever2_ef'
  | 'lever3_savings_redirect'
  | 'lever4_isa'
  | 'lever5_pension_personal'
  | 'lever6_mortgage_op'
  | 'lever6b_employer_pension'; // Session 2 lever

export interface StandingOrder {
  label: string;
  amount: number;
  recipient: string;
  startDate: string;
  endDate?: string;           // if time-limited (e.g. EF until complete)
  note?: string;
}

export interface SinkingFundItem {
  id: string;
  label: string;
  group: 'Transport' | 'Home' | 'Lifestyle' | 'OMB' | 'Tax' | 'Other';
  monthly: number;
}

export interface SavingsAccount {
  name: string;
  balance: number;
  rate: number;      // AER as percentage (e.g. 3.5)
}

export interface ConsumerDebt {
  name: string;
  balance: number;
  apr: number;           // APR as percentage (e.g. 22.9)
  monthlyMinimum: number;
}

export interface GapHQLevers {
  leadsPerMonth: number;
  conversionRatePct: number;
  avgTransactionValue: number;
  transactionsPerClientPerYear: number;
  grossMarginPct: number;
  fixedCostMonthly: number;
  variableCostPct: number;
}

// ── Main interface ───────────────────────────────────────────────────────────

export interface ClientData {

  // ── Session meta ─────────────────────────────────────────────────────────
  clientName: string;
  clientEmail: string;
  businessName: string;
  adviserName: string;
  sessionDate: string;        // DD/MM/YYYY — drives tax year auto-detection
  sessionType: 'new' | 's2' | 's3' | 'annual';
  taxYearOverride?: TaxYear;  // manual override; auto-detected from sessionDate if absent
  voyantBreak1Complete: boolean; // after Movement 4
  voyantBreak2Complete: boolean; // after levers

  // ── Xero connection ──────────────────────────────────────────────────────
  xeroTenantId?: string;       // bound at connect time; identifies Xero org
  xeroTenantName?: string;     // display only

  // ── Business DLA (populated by /api/xero/balance in S2) ──────────────────
  // Sign convention: positive = company owes director (asset for director),
  //                  negative = director owes company (overdrawn — liability).
  // Surfaced in S1 Baby Steps as an extra liability row when negative.
  businessDlaBalance?: number;

  // ── S1 snapshot (captured at S1 open, never overwritten) ─────────────────
  // Records the freedom age the client opened S1 with so the S2 "what's
  // changed" comparison can show the full arc.
  s1FreedomAgeBefore?: number;

  // ── S2 agreed commitments (input on S2 screens) ──────────────────────────
  s2PensionMonthly?: number;             // employer pension agreed in S2
  s2DividendMonthly?: number;            // dividend SO agreed in S2
  s2CtReserveMonthly?: number;           // CT reserve SO
  s2VatReserveMonthly?: number;          // VAT reserve SO if applicable
  s2DebtorCollectionTarget?: number;     // % of aged debtors to collect
  s2DebtorCollectionDate?: string;       // target date
  s2BusinessEfTarget?: number;           // months of cover (default 3)
  s2LeadsPerMonth?: number;              // manual input if not tracked
  s2ConversionRate?: number;             // manual input if not tracked
  s2RevenueGrowthRate?: number;          // projection assumption
  s2OverheadGrowthRate?: number;         // projection assumption (default 0.025)
  s2DlaRepaymentRoute?: 'dividend' | 'formal' | 'credit-reduce-dividends' | null;
  s2DlaMonthlyRepayment?: number;
  s2HistoricCtLiabilityClearanceMonths?: number;
  s2FreedomAgeBefore?: number;           // recorded at S2 open
  s2FreedomAgeAfter?: number;            // recorded at S2 close
  s2GapBefore?: number;
  s2GapAfter?: number;
  // Adviser free-text items on the S2 Business Action Plan, persisted across
  // sessions. Each entry has its own id so individual items can be edited
  // or removed without index collisions.
  s2ActionPlanItems?: Array<{
    id: string;
    section: 'immediate' | '30d' | '90d' | 'ongoing';
    text: string;
  }>;

  // ── Client profile ───────────────────────────────────────────────────────
  clientAge: number;
  partnerAge: number;
  isCouple: boolean;
  targetFreedomAge: number;   // REQUIRED — most important single input

  // ── Income ────────────────────────────────────────────────────────────────
  salary: number;             // annual
  dividends: number;          // annual (company dividends)
  rentalIncomeNet: number;    // annual net after expenses
  savingsInterest: number;    // annual above PSA / outside ISA
  otherDividends: number;     // dividends from other shareholdings
  payeIncome: number;         // from separate employment
  expensesReimbursement: number; // monthly, tax-free pass-through
  taxPaymentMethod: 'monthly' | 'selfAssessment';
  studentLoanUndergrad: 'none' | 'plan1' | 'plan2' | 'plan5';
  studentLoanPostgrad: boolean;

  // ── Personal debt (Baby Steps Step 2) ─────────────────────────────────────
  consumerDebts: ConsumerDebt[];
  // Backward compat (derived from consumerDebts)
  consumerDebtTotal: number;
  consumerDebtMonthlyPayment: number;
  consumerDebtHighestAPR: number;
  consumerDebtLabel: string;

  // ── Sinking funds ─────────────────────────────────────────────────────────
  sinkingFundsTotal: number;
  sinkingFundsDetail: SinkingFundItem[];

  // ── Emergency fund ────────────────────────────────────────────────────────
  efAccount1Name: string;
  efAccount1Balance: number;
  efAccount1Rate: number;
  efAccount1Monthly: number;
  efAccount2Name: string;
  efAccount2Balance: number;
  efAccount2Rate: number;
  efAccount2Monthly: number;
  efTargetMonths: number;     // 3 standard; 6 for variable income directors

  // ── Pension ───────────────────────────────────────────────────────────────
  pensionBalance: number;
  pensionProvider: string;
  pensionMonthly: number;           // personal contribution
  pensionCompanyFunded: number;     // current employer contribution
  pensionSteppedSchedule: PensionStep[]; // future stepped amounts by year
  pensionAnnualAllowanceUsed: number;    // current year, to flag carry forward

  // ── ISA ───────────────────────────────────────────────────────────────────
  isaBalance: number;
  investmentsBalance: number; // S&S ISA / investments pot — use for growth projections
  isaContribution: number;    // monthly SO

  // ── Mortgage (residential) ────────────────────────────────────────────────
  propertyValue: number;
  mortgageBalance: number;
  mortgageRate: number;
  mortgageTermYears: number;
  mortgageFixEndDate: string; // MM/YYYY
  mortgagePayment: number;    // standard monthly
  mortgageOP: number;         // monthly overpayment SO
  mortgageErcFreeAnnual: number; // ERC-free annual lump sum limit
  mortgageIsInterestOnly: boolean; // residential interest-only flag
  mortgageAnnualLumpSum?: number;  // annual lump sum for mortgage scenario C (manual)

  // ── Rental property (if applicable) ───────────────────────────────────────
  rentalPropertyValue: number;
  rentalMortgageBalance: number;
  rentalMortgageRate: number;
  rentalMortgageTerm: number;          // years (repayment only)
  rentalMortgageType: 'repayment' | 'interestOnly';
  rentalMortgageIsInterestOnly: boolean;
  rentalMortgagePayment: number;       // auto-calculated monthly

  // ── DLA (Director's Loan Account) ─────────────────────────────────────────
  dlaBalance: number;
  dlaRepaymentSchedule: DLAPayment[];

  // ── Non-EF savings accounts ──────────────────────────────────────────────
  savingsAccounts: SavingsAccount[];
  // Backward compat (deprecated — use savingsAccounts instead)
  otherSavingsBalance: number;
  otherSavingsLabel: string;
  otherSavingsRate: number;

  // ── DB (Defined Benefit) pension ──────────────────────────────────────────
  hasDbPension: boolean;
  dbPensionAnnualIncome: number;   // projected annual income at retirement
  dbPensionStartAge: number;
  dbPensionIndexLinked: boolean;
  dbPensionProvider: string;

  // ── Forever Home / Upsizing ─────────────────────────────────────────────
  foreverHomeTargetValue?: number;
  foreverHomeTargetYear?: number;

  // ── Assumptions (growth rates for projections) ───────────────────────────
  assumptionPensionGrowth: number;   // default 8.0%
  assumptionIsaGrowth: number;       // default 8.0%
  assumptionCashRate: number;        // default 1.0%
  assumptionPropertyGrowth: number;  // default 1.0%
  assumptionInflation: number;       // default 2.5%

  // ── Retirement / Freedom Number ───────────────────────────────────────────
  plsaStandard: 'comfortable' | 'moderate' | 'minimum' | null;
  rlsaMode: 'plsa' | 'percentage';   // PLSA standard vs % of income
  phase1IncomePct: number;            // % of gross for phase 1 (comfortable)
  phase2IncomePct: number;            // % of gross for phase 2 (moderate)
  phase3IncomePct: number;            // % of gross for phase 3 (minimum)
  phase1Income: number;               // £/yr derived from % or PLSA
  phase2Income: number;               // £/yr derived from % or PLSA
  phase3Income: number;               // £/yr derived from % or PLSA
  statePensionAnnual: number; // estimated full new state pension
  statePensionAge: number;    // default 67
  // Calculated fields — derived, not entered:
  freedomNumberToday: number;
  freedomNumberInflated: number;
  annualRetirementIncomeNeeded: number;
  gapAmount: number;

  // ── Voyant captures ───────────────────────────────────────────────────────
  voyantDoNothing: VoyantCapture;   // pre-session, locked permanently
  voyantPostS1: VoyantCapture;      // after Session 1 Voyant break
  voyantPostS2: VoyantCapture;      // after Session 2 Voyant break

  // ── Lever decisions ───────────────────────────────────────────────────────
  leversApplied: LeverDecision[];

  // ── Session close ─────────────────────────────────────────────────────────
  standingOrders: StandingOrder[];
  nextActions: [string, string, string]; // exactly three
  session2Date: string;
  session3Date?: string;                 // captured at S2 close, fed into S3 intro
  sessionNotes: string;

  // ── Business (Session 2) ──────────────────────────────────────────────────
  businessRevenueMRR: number;
  businessCostsMonthly: number;
  businessBankBalance: number;
  businessCtProvisionHeld: number;
  businessVatProvisionHeld: number;
  businessEFMonths: number;
  gapHQLevers: GapHQLevers;

  // ── Session 2 — Business Position ──────────────────────────────────────────
  businessMonthlyRevenue: number;
  businessGrossMarginPct: number;
  businessFixedMonthlyCosts: number;
  businessVariableCostPct: number;
  businessOperatingBufferBalance: number;
  businessTaxReserveBalance: number;
  businessEstimatedCTLiability: number;
  businessDebtorDays: number;
  businessDLABalance: number;
  businessRevenueConcentration: 'low' | 'medium' | 'high';
  businessClientCount: number;
  businessAvgMonthlyValue: number;
  businessAvgRetentionMonths: number;

  // ── Session 2 — Lever commitments ─────────────────────────────────────────
  leverClientsTargetPct: number;
  leverAvgValueTargetPct: number;
  leverRetentionTargetPct: number;
  leverMarginTargetPct: number;
  leverFixedCostReductionMonthly: number;
  leverVariableCostTargetPct: number;

  // ── Session 2 — Cash levers ───────────────────────────────────────────────
  targetDebtorDays: number;
  billingFrequencyImprovement: number;
  hmrcReserveMonthlySO: number;

  // ── Session 2 — CT levers ────────────────────────────────────────────────
  legitimateExpensesMonthly: number;

  // ── Session 2 — Voyant read-back ──────────────────────────────────────────
  voyantS2RetirementAge: number;
  voyantS2PotAtFreedom: number;
  voyantS2AnnualIncome: number;
  voyantS2GapRemaining: number;

  // ── Session 2 — Running Tally ─────────────────────────────────────────────
  s2AdditionalProfitMonthly: number;
  s2CtSavedAnnual: number;
  s2CtSavedCumulative: number;
  s2PensionFundedMonthly: number;
  s2FreedomAgeImprovement: number;

  // ── Your Plan — phased decisions (Brief 6) ───────────────────────────────
  estimatedCompanyProfit: number;     // gross profit for CT rate calc
  planEfTargetMonths: number;         // 3, 6, or custom
  planEfMonthlyTopUp: number;         // monthly contribution to EF
  planEfRedirectTo: 'isa' | 'pension' | 'mortgage' | 'savings';
  planEfCompleteDate: string;         // ISO date — calculated
  planEfAdjustedOutgoings: number;    // adviser-adjusted monthly outgoings basis (Brief 46B S6)
  planSavingsGoals: Array<{ name: string; amount: number; timelineMonths: number }>; // Brief 46B S6

  planDividendReduction: number;      // £/month drawn less from business
  planPensionFromDividendReduction: number; // grossed-up pension equivalent
  planDividendReductionCommitted: boolean;

  planMortgageOverpayment: number;    // £/month
  planMortgageAnnualLumpSum: number;  // £/year optional
  planMortgagePayoffAge: number;      // calculated
  planMortgagePayoffDate: string;     // calculated ISO date
  planMortgageInterestSaved: number;  // calculated
  planMortgageRedirectTo: 'isa' | 'pension' | 'savings';

  planIsaMonthlyContribution: number;
  planIsaRedirectFromMortgage: boolean;

  planDebtPayoffSchedule: Array<{
    debtId: string;
    payoffDate: string;
    monthlyFreed: number;
    redirectTo: 'nextDebt' | 'isa' | 'pension' | 'mortgage' | 'savings';
  }>;

  planDebtExtraMonthly: number;       // extra above minimums for snowball
  planFinalMonthlyCommitted: number;  // sum of all SOs from plan
  planBuilt: boolean;                 // true when phased plan screen reached
  planTaxSavedPerYear: number;        // annual tax saved from dividend reduction
  planCumulativeTaxSaved: number;     // cumulative tax saved to retirement

  // ── Voyant S1 scenario — read back after Voyant Break 2 ────────────────
  voyantS1RetirementAge: number;          // new projected retirement age with S1 decisions
  voyantS1PotAtTargetAge: number;         // new pot at freedom age with S1 decisions
  voyantS1LumpSumNeeded: number;          // remaining lump sum shortfall after S1
  voyantS1AnnualSavingsNeeded: number;    // remaining annual savings needed — the S2 target
  voyantS1Captured: boolean;              // true once adviser has entered these values

  // ── Brief 12 — Spending analysis ────────────────────────────────────────────
  essentialsUtilities: number;        // monthly utilities
  essentialsFood: number;             // monthly food/groceries
  essentialsTravel: number;           // monthly travel (commute, fuel)
  lifestyleMonthly: number;           // remainder after essentials — derived, not entered
  takeHomeReduction: number;          // voluntary reduction to fund pension lever
  lifestyleRedirectTo: 'pension' | 'mortgage' | 'isa' | '';

  // ── Brief 12 — ONS benchmarking ───────────────────────────────────────────
  clientAgeBand: '35-39' | '40-44' | '45-49' | '50-54' | '55-59' | '60-64' | '';

  // ── Brief 12 — Running tally (calculated, not entered) ────────────────────
  totalTaxSavedAnnual: number;
  totalTaxSavedCumulative: number;
  totalInterestSaved: number;
  freedomAgeImprovement: number;      // years brought forward
  totalGapClosed: number;

  // ── Brief 12 — RLSA target ────────────────────────────────────────────────
  rlsaAnnualTarget: number;

  // ── Brief 12 — Voyant S1 read-back additional fields ──────────────────────
  voyantS1AnnualIncome: number;       // annual income at freedom from Voyant
  voyantS1GapRemaining: number;       // gap remaining after S1 decisions

  // ── Backward-compat fields for existing v4 screens ────────────────────────
  // These will be removed as screens are rebuilt in later phases.
  otherIncome: number;        // → will split into rentalIncomeNet + savingsInterest + otherDividends + payeIncome
  livingCosts: number;        // → will be derived or removed
  monthlyHMRC: number;        // → will be derived from tax calculation
  efTopUp: number;            // → will be derived from EF lever

  // ── PARTNER INCOME ──────────────────────────────────────────────────────────
  // Fully mirrors client income fields
  partnerName: string;
  partnerSalary: number;
  partnerDividends: number;
  partnerOtherIncome: number;                // PAYE from other employment, freelance etc
  partnerExpensesReimbursement: number;      // tax-free pass-through from company
  partnerTaxPaymentMethod: 'monthly' | 'selfAssessment' | 'accountant' | 'none';
  partnerMonthlyHMRC: number;

  // ── PARTNER PENSIONS ─────────────────────────────────────────────────────────
  // Fully mirrors client pension fields
  partnerPensionBalanceDC: number;           // all DC/money purchase pots pooled
  partnerHasDBPension: boolean;
  partnerDBPensionScheme: string;
  partnerDBPensionAccruedAnnual: number;     // annual pension amount at NRA
  partnerDBPensionNRA: number;               // normal retirement age for DB scheme
  partnerEmployerPensionMonthly: number;

  // ── PARTNER ISA & INVESTMENTS ───────────────────────────────────────────────
  // Fully mirrors client ISA fields
  partnerISACash: number;
  partnerISAStocksShares: number;
  partnerISAMonthly: number;
  partnerOtherInvestments: number;           // GIA, premium bonds etc

  // ── CLIENT ISA SPLIT (replaces flat isaBalance — keep isaBalance for compat) ─
  isaCash: number;                           // cash ISA — no growth assumption
  isaStocksShares: number;                   // S&S ISA — 6% growth assumption

  // ── CLIENT PENSION DC / DB ───────────────────────────────────────────────────
  // (keep pensionBalance for compat — set = pensionBalanceDC)
  pensionBalanceDC: number;                  // all DC/money purchase pots pooled
  hasDBPension: boolean;
  dbPensionScheme: string;                   // scheme name(s)
  dbPensionAccruedAnnual: number;            // annual pension at NRA
  dbPensionNRA: number;                      // normal retirement age

  // ── RENTAL PROPERTIES (simplified aggregates) ────────────────────────────────
  rentalPropertyCount: number;               // how many properties
  rentalEquityTotal: number;                 // aggregate equity across all rental properties
  rentalMonthlyGrossIncome: number;          // total gross monthly rent received
  rentalMonthlyMortgageCosts: number;        // total monthly rental mortgage payments
  rentalMonthlyRunningCosts: number;         // maintenance, management, insurance aggregate

  // ── CHILDREN'S COMMITTED COSTS ──────────────────────────────────────────────
  childMonthlyCommittedCosts: number;        // aggregate monthly: school fees, uni support
  childCostsEndAge: number;                  // client age when these costs stop

  // ── EIS / SEIS ───────────────────────────────────────────────────────────────
  eisSeisBalance: number;                    // approximate total — illiquid, balance sheet only

  // ── SESSION METADATA ─────────────────────────────────────────────────────────
  intakeNotes: string;                       // free text from end of intake form
  intakeSubmittedAt: string;                 // ISO date string from Tally submission

  // ─── SESSION 3 SETUP — SCENARIO TOGGLES ───────────────────────────────────────
  s3ScenarioEarlierOn: boolean;
  s3ScenarioEarlierOverride: boolean;
  s3ScenarioPhasedOn: boolean;
  s3ScenarioPhasedOverride: boolean;
  s3ScenarioOneOffOn: boolean;
  s3ScenarioOneOffOverride: boolean;
  s3ScenarioStressOn: boolean;
  s3ScenarioLongevityOn: boolean;
  s3ScenarioLifestyleOn: boolean;

  // ─── SESSION 3 SCENARIO INPUTS ────────────────────────────────────────────────
  s3EarlierRetirementAge: number;
  s3PhasedStartAge: number;
  s3PhasedEndAge: number;
  s3PhasedMonthlyIncome: number;
  s3OneOffDescription: string;
  s3OneOffAmount: number;
  s3OneOffAtAge: number;
  s3LifestyleBusinessMonthlyIncome: number;

  // ─── SESSION 3 SCENARIO SCREEN PERSISTENCE (optional) ─────────────────────────
  // Written back from PhasedRetirement.tsx + OneOffSpend.tsx so picks survive nav.
  s3PhasedAge?: number;                      // coast-from age picked on Phased screen
  s3SpendAmount?: number;                    // £ amount picked on OneOffSpend screen
  s3SpendAge?: number;                       // age at which the spend occurs
  s3SpendSource?: 'isa' | 'cash' | 'pension'; // funding source for the spend

  // ─── SESSION 3 VOYANT READ-BACKS ──────────────────────────────────────────────
  s3EarlierFreedomAge: number;
  s3EarlierAdditionalMonthly: number;
  s3PhasedFreedomAge: number;
  s3PhasedVoyantNote: string;
  s3OneOffFreedomAge: number;
  s3OneOffFundedNote: string;
  s3StressTestResult: 'funded' | 'shortfall' | '';
  s3StressTestNote: string;
  s3LongevityFundedToAge: number;
  s3LifestylePotReduction: number;

  // Final Voyant confirm read-backs
  s3FinalFreedomAge: number;
  s3FinalPotAtFreedom: number;
  s3FinalMonthlyDrawdown: number;
  s3FinalFundedToAge: number;
  s3FinalStressResult: 'funded' | 'shortfall' | '';

  // ─── LIFE DESIGN ──────────────────────────────────────────────────────────────
  s3ScenariosCommitted: string[];
  s3BusinessAtFreedom: 'sold' | 'wound-down' | 'lifestyle' | 'handed-over' | '';
  s3LegacyNote: string;
  s3LifeDesignStatement: string;

  // ─── RETAINER / ANNUAL REVIEW ─────────────────────────────────────────────────
  retainerFee: number;
  retainerStartDate: string;
  annualReviewDate: string;
  s3HomeworkAction1: string;
  s3HomeworkAction2: string;
}

// ── Empty Voyant capture ─────────────────────────────────────────────────────

const EMPTY_VOYANT: VoyantCapture = {
  retirementAge: null,
  potAtRetirement: null,
  annualIncomeAchievable: null,
  voyantLumpSumNeeded: null,
  voyantAnnualSavingsNeeded: null,
  capturedDate: '',
  voyantAssetsAtTarget: null,
  voyantLiabilitiesAtTarget: null,
  voyantNetWorthAtTarget: null,
};

// ── Default (blank) client ───────────────────────────────────────────────────

export const BLANK_CLIENT: ClientData = {
  // Session meta
  clientName: '',
  clientEmail: '',
  businessName: '',
  adviserName: '',
  sessionDate: new Date().toLocaleDateString('en-GB'),
  sessionType: 'new',
  taxYearOverride: undefined,
  voyantBreak1Complete: false,
  voyantBreak2Complete: false,

  // Xero
  xeroTenantId: undefined,
  xeroTenantName: undefined,

  // Client profile
  clientAge: 40,
  partnerAge: 0,
  isCouple: false,
  targetFreedomAge: 55,

  // Income
  salary: 12570,
  dividends: 0,
  rentalIncomeNet: 0,
  savingsInterest: 0,
  otherDividends: 0,
  payeIncome: 0,
  expensesReimbursement: 0,
  taxPaymentMethod: 'monthly',
  studentLoanUndergrad: 'none',
  studentLoanPostgrad: false,

  // Debt
  consumerDebts: [{ name: '', balance: 0, apr: 0, monthlyMinimum: 0 }],
  consumerDebtTotal: 0,
  consumerDebtMonthlyPayment: 0,
  consumerDebtHighestAPR: 0,
  consumerDebtLabel: '',

  // Sinking funds
  sinkingFundsTotal: 0,
  sinkingFundsDetail: [],

  // Emergency fund
  efAccount1Name: '',
  efAccount1Balance: 0,
  efAccount1Rate: 0,
  efAccount1Monthly: 0,
  efAccount2Name: '',
  efAccount2Balance: 0,
  efAccount2Rate: 0,
  efAccount2Monthly: 0,
  efTargetMonths: 3,

  // Pension
  pensionBalance: 0,
  pensionProvider: '',
  pensionMonthly: 0,
  pensionCompanyFunded: 0,
  pensionSteppedSchedule: [],
  pensionAnnualAllowanceUsed: 0,

  // ISA
  isaBalance: 0,
  investmentsBalance: 0,
  isaContribution: 0,

  // Mortgage
  propertyValue: 0,
  mortgageBalance: 0,
  mortgageRate: 0,
  mortgageTermYears: 0,
  mortgageFixEndDate: '',
  mortgagePayment: 0,
  mortgageOP: 0,
  mortgageErcFreeAnnual: 0,
  mortgageIsInterestOnly: false,

  // Rental
  rentalPropertyValue: 0,
  rentalMortgageBalance: 0,
  rentalMortgageRate: 0,
  rentalMortgageTerm: 0,
  rentalMortgageType: 'repayment',
  rentalMortgageIsInterestOnly: false,
  rentalMortgagePayment: 0,

  // DLA
  dlaBalance: 0,
  dlaRepaymentSchedule: [],

  // Non-EF savings
  savingsAccounts: [{ name: '', balance: 0, rate: 0 }],
  otherSavingsBalance: 0,
  otherSavingsLabel: '',
  otherSavingsRate: 0,

  // DB Pension
  hasDbPension: false,
  dbPensionAnnualIncome: 0,
  dbPensionStartAge: 67,
  dbPensionIndexLinked: false,
  dbPensionProvider: '',

  // Forever Home
  foreverHomeTargetValue: undefined,
  foreverHomeTargetYear: undefined,

  // Assumptions
  assumptionPensionGrowth: 0.08,
  assumptionIsaGrowth: 0.08,
  assumptionCashRate: 0.01,
  assumptionPropertyGrowth: 0.01,
  assumptionInflation: 0.025,

  // Freedom Number
  plsaStandard: 'comfortable',
  rlsaMode: 'plsa' as const,
  phase1IncomePct: 0,
  phase2IncomePct: 0,
  phase3IncomePct: 0,
  phase1Income: 0,
  phase2Income: 0,
  phase3Income: 0,
  statePensionAnnual: 11500,
  statePensionAge: 67,
  freedomNumberToday: 0,
  freedomNumberInflated: 0,
  annualRetirementIncomeNeeded: 0,
  gapAmount: 0,

  // Voyant
  voyantDoNothing: { ...EMPTY_VOYANT },
  voyantPostS1: { ...EMPTY_VOYANT },
  voyantPostS2: { ...EMPTY_VOYANT },

  // Levers
  leversApplied: [],

  // Session close
  standingOrders: [],
  nextActions: ['', '', ''],
  session2Date: '',
  sessionNotes: '',

  // Business (S2)
  businessRevenueMRR: 0,
  businessCostsMonthly: 0,
  businessBankBalance: 0,
  businessCtProvisionHeld: 0,
  businessVatProvisionHeld: 0,
  businessEFMonths: 3,

  // Session 2
  businessMonthlyRevenue: 0,
  businessGrossMarginPct: 0,
  businessFixedMonthlyCosts: 0,
  businessVariableCostPct: 0,
  businessOperatingBufferBalance: 0,
  businessTaxReserveBalance: 0,
  businessEstimatedCTLiability: 0,
  businessDebtorDays: 0,
  businessDLABalance: 0,
  businessRevenueConcentration: 'low' as const,
  businessClientCount: 0,
  businessAvgMonthlyValue: 0,
  businessAvgRetentionMonths: 0,
  leverClientsTargetPct: 0,
  leverAvgValueTargetPct: 0,
  leverRetentionTargetPct: 0,
  leverMarginTargetPct: 0,
  leverFixedCostReductionMonthly: 0,
  leverVariableCostTargetPct: 0,
  targetDebtorDays: 0,
  billingFrequencyImprovement: 0,
  hmrcReserveMonthlySO: 0,
  legitimateExpensesMonthly: 0,
  voyantS2RetirementAge: 0,
  voyantS2PotAtFreedom: 0,
  voyantS2AnnualIncome: 0,
  voyantS2GapRemaining: 0,
  s2AdditionalProfitMonthly: 0,
  s2CtSavedAnnual: 0,
  s2CtSavedCumulative: 0,
  s2PensionFundedMonthly: 0,
  s2FreedomAgeImprovement: 0,
  gapHQLevers: {
    leadsPerMonth: 0,
    conversionRatePct: 0,
    avgTransactionValue: 0,
    transactionsPerClientPerYear: 0,
    grossMarginPct: 0,
    fixedCostMonthly: 0,
    variableCostPct: 0,
  },

  // Your Plan — phased decisions
  estimatedCompanyProfit: 0,
  planEfTargetMonths: 6,
  planEfMonthlyTopUp: 0,
  planEfRedirectTo: 'isa',
  planEfCompleteDate: '',
  planEfAdjustedOutgoings: 0,
  planSavingsGoals: [],

  planDividendReduction: 0,
  planPensionFromDividendReduction: 0,
  planDividendReductionCommitted: false,

  planMortgageOverpayment: 0,
  planMortgageAnnualLumpSum: 0,
  planMortgagePayoffAge: 0,
  planMortgagePayoffDate: '',
  planMortgageInterestSaved: 0,
  planMortgageRedirectTo: 'isa',

  planIsaMonthlyContribution: 0,
  planIsaRedirectFromMortgage: false,

  planDebtPayoffSchedule: [],
  planDebtExtraMonthly: 0,
  planFinalMonthlyCommitted: 0,
  planBuilt: false,
  planTaxSavedPerYear: 0,
  planCumulativeTaxSaved: 0,

  // Voyant S1 scenario
  voyantS1RetirementAge: 0,
  voyantS1PotAtTargetAge: 0,
  voyantS1LumpSumNeeded: 0,
  voyantS1AnnualSavingsNeeded: 0,
  voyantS1Captured: false,

  // Brief 12 — Spending analysis
  essentialsUtilities: 0,
  essentialsFood: 0,
  essentialsTravel: 0,
  lifestyleMonthly: 0,
  takeHomeReduction: 0,
  lifestyleRedirectTo: '',

  // Brief 12 — ONS benchmarking
  clientAgeBand: '',

  // Brief 12 — Running tally
  totalTaxSavedAnnual: 0,
  totalTaxSavedCumulative: 0,
  totalInterestSaved: 0,
  freedomAgeImprovement: 0,
  totalGapClosed: 0,

  // Brief 12 — RLSA target
  rlsaAnnualTarget: 0,

  // Brief 12 — Voyant S1 read-back additional
  voyantS1AnnualIncome: 0,
  voyantS1GapRemaining: 0,

  // Backward-compat
  otherIncome: 0,
  livingCosts: 0,
  monthlyHMRC: 0,
  efTopUp: 0,

  // Partner income
  partnerName: '',
  partnerSalary: 0,
  partnerDividends: 0,
  partnerOtherIncome: 0,
  partnerExpensesReimbursement: 0,
  partnerTaxPaymentMethod: 'none' as const,
  partnerMonthlyHMRC: 0,

  // Partner pensions
  partnerPensionBalanceDC: 0,
  partnerHasDBPension: false,
  partnerDBPensionScheme: '',
  partnerDBPensionAccruedAnnual: 0,
  partnerDBPensionNRA: 67,
  partnerEmployerPensionMonthly: 0,

  // Partner ISA
  partnerISACash: 0,
  partnerISAStocksShares: 0,
  partnerISAMonthly: 0,
  partnerOtherInvestments: 0,

  // ISA split
  isaCash: 0,
  isaStocksShares: 0,

  // Pension DC/DB
  pensionBalanceDC: 0,
  hasDBPension: false,
  dbPensionScheme: '',
  dbPensionAccruedAnnual: 0,
  dbPensionNRA: 67,

  // Rental
  rentalPropertyCount: 0,
  rentalEquityTotal: 0,
  rentalMonthlyGrossIncome: 0,
  rentalMonthlyMortgageCosts: 0,
  rentalMonthlyRunningCosts: 0,

  // Children
  childMonthlyCommittedCosts: 0,
  childCostsEndAge: 0,

  // EIS/SEIS
  eisSeisBalance: 0,

  // Metadata
  intakeNotes: '',
  intakeSubmittedAt: '',

  // Session 3 — Scenario toggles
  s3ScenarioEarlierOn: false,
  s3ScenarioEarlierOverride: false,
  s3ScenarioPhasedOn: false,
  s3ScenarioPhasedOverride: false,
  s3ScenarioOneOffOn: false,
  s3ScenarioOneOffOverride: false,
  s3ScenarioStressOn: true,
  s3ScenarioLongevityOn: true,
  s3ScenarioLifestyleOn: true,

  // Session 3 — Scenario inputs
  s3EarlierRetirementAge: 0,
  s3PhasedStartAge: 0,
  s3PhasedEndAge: 0,
  s3PhasedMonthlyIncome: 0,
  s3OneOffDescription: '',
  s3OneOffAmount: 0,
  s3OneOffAtAge: 0,
  s3LifestyleBusinessMonthlyIncome: 0,

  // Session 3 — Voyant read-backs
  s3EarlierFreedomAge: 0,
  s3EarlierAdditionalMonthly: 0,
  s3PhasedFreedomAge: 0,
  s3PhasedVoyantNote: '',
  s3OneOffFreedomAge: 0,
  s3OneOffFundedNote: '',
  s3StressTestResult: '',
  s3StressTestNote: '',
  s3LongevityFundedToAge: 0,
  s3LifestylePotReduction: 0,

  // Session 3 — Final confirm
  s3FinalFreedomAge: 0,
  s3FinalPotAtFreedom: 0,
  s3FinalMonthlyDrawdown: 0,
  s3FinalFundedToAge: 0,
  s3FinalStressResult: '',

  // Life Design
  s3ScenariosCommitted: [],
  s3BusinessAtFreedom: '',
  s3LegacyNote: '',
  s3LifeDesignStatement: '',

  // Retainer / Annual Review
  retainerFee: 0,
  retainerStartDate: '',
  annualReviewDate: '',
  s3HomeworkAction1: '',
  s3HomeworkAction2: '',
};
// v10.3 — Session 3: Life Design fields added 08/04/2026
